// src/pages/ProfilePage.js

import React, { useEffect, useState } from "react";
import { reverseGeocode } from "../api/reverseGeocodeClient";
import { Link } from "react-router-dom";
import { signInWithGoogle, useAuthWait, handleLogout } from "../auth";

import AWS from "aws-sdk";
import { saveLivingHarmonyProfile } from "../GeoVisionAI";

import { getProfile } from "../api/ProfileOntologyManager";
import "./ProfilePage.css";

function normalizeProfileToForm(loaded, fallbackUser) {
  if (!loaded) {
    return {
      fullName: fallbackUser?.displayName || "",
      email: fallbackUser?.email || "",
      location: "",
      homeType: "",
      roomsToDesign: ["Bedroom", "Home Office"],
      livingWith: [],
      notes: "",
    };
  }

  // Accept any of: { form: {...} }, { userProfile: {...} }, or flat fields
  const src = loaded.form || loaded.userProfile || loaded;
  return {
    fullName: src.fullName ?? fallbackUser?.displayName ?? "",
    email: src.email ?? fallbackUser?.email ?? "",
    location: src.location ?? "",
    homeType: src.homeType ?? "",
    roomsToDesign: Array.isArray(src.roomsToDesign)
      ? src.roomsToDesign
      : ["Bedroom", "Home Office"],
    livingWith: Array.isArray(src.livingWith) ? src.livingWith : [],
    notes: src.notes ?? "",
  };
}

export default function ProfilePage() {
  const [userId, setUserId] = useState(null);

  // Camera + image capture
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraDropdown, setCameraDropdown] = useState({
    roomId: null,
    open: null,
  });

  const [isCreating, setIsCreating] = useState(false);
  const [isLinkedInInApp, setIsLinkedInInApp] = useState(false);

  const [roomOptions, setRoomOptions] = useState([
    {
      id: "Bedroom",
      title: "Bedroom",
      description: "Create a restful, rejuvenating sleep space.",
      image: null,
    },
    {
      id: "Home Office",
      title: "Home Office",
      description: "Design a workspace that boosts creativity and focus.",
      image: null,
    },
    {
      id: "Living Room",
      title: "Living Room",
      description: "Turn your living room into a cozy, welcoming retreat.",
      image: null,
    },
    {
      id: "Kitchen",
      title: "Kitchen",
      description: "Optimize your kitchen for both beauty and function.",
      image: null,
    },
    {
      id: "Bathroom",
      title: "Bathroom",
      description: "Refresh your bathroom with calming design touches.",
      image: null,
    },
    {
      id: "Entryway",
      title: "Entryway",
      description: "Make a great first impression at the front door.",
      image: null,
    },
  ]);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    location: "",
    homeType: "",
    roomsToDesign: ["Bedroom", "Home Office"],
    livingWith: [],
    notes: "",
  });

  // ── fetch the user’s city/metro (or fallback to coords) ────────────
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        try {
          const locMap = await reverseGeocode(latitude, longitude);
          const place_name =
            (locMap.house_number ? `${locMap.house_number} ` : "") +
            (locMap.road || "") +
            (locMap.town ? `, ${locMap.town}` : "") +
            (locMap.state ? `, ${locMap.state}` : "") +
            (locMap.postcode ? `, ${locMap.postcode}` : "");

          const pretty =
            place_name.trim() ||
            locMap.metro ||
            locMap.city ||
            `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;
          setForm((prev) => ({ ...prev, location: pretty }));
        } catch {
          setForm((prev) => ({
            ...prev,
            location: `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`,
          }));
        }
      },
      () => setForm((prev) => ({ ...prev, location: "Unknown" })),
    );
  }, []);
  // ───────────────────────────────────────────────────────────────────

  const { user, loading } = useAuthWait(async (u, creds) => {
    const uid = creds.identityId;
    setUserId(uid);
    try {
      const loaded = await getProfile(uid, creds);

      // Normalize fields into current form state
      const nextForm = normalizeProfileToForm(loaded, u);
      setForm((prev) => ({ ...prev, ...nextForm }));

      // Prefer roomOptions if present; otherwise transform "rooms" object into options
      if (Array.isArray(loaded?.roomOptions) && loaded.roomOptions.length) {
        setRoomOptions(loaded.roomOptions);
      } else if (loaded?.rooms && typeof loaded.rooms === "object") {
        // Example input shape (based on your earlier JSON):
        // rooms: { "Bedroom": { title, description, image }, ... }
        const opts = Object.values(loaded.rooms).map((r) => ({
          id: r.title || r.id || r.name,
          title: r.title || r.id || r.name,
          description: r.description || "",
          image: r.image || null,
        }));
        if (opts.length) setRoomOptions(opts);
      }
    } catch (err) {
      console.error(
        "[ProfilePage] Failed to load profile via getProfile:",
        err,
      );
      // Fallback to basic identity if loading fails
      setForm((prev) => ({
        ...prev,
        fullName: u?.displayName || "",
        email: u?.email || "",
      }));
    }
  });
  useEffect(() => {
    if (navigator.userAgent.match(/LinkedInApp/i)) {
      setIsLinkedInInApp(true);
    }
    if (user) {
      setForm((prev) => ({
        ...prev,
        fullName: user.displayName || "",
        email: user.email || "",
      }));
    }
  }, [user]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && Array.isArray(form[name])) {
      setForm((prev) => {
        const already = prev[name].includes(value);
        return {
          ...prev,
          [name]: already
            ? prev[name].filter((v) => v !== value)
            : [...prev[name], value],
        };
      });
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  }

  function updateRoomImage(roomId, imageDataUrl) {
    setRoomOptions((prev) =>
      prev.map((room) =>
        room.id === roomId ? { ...room, image: imageDataUrl } : room,
      ),
    );
  }

  function handleCameraOption(roomId, option) {
    setCameraDropdown({ roomId: null, open: null });
    if (option === "camera") {
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode } })
        .then((stream) => {
          const video = document.createElement("video");
          video.srcObject = stream;
          video.play();
          video.className = "camera-preview";

          const modal = document.createElement("div");
          modal.className = "camera-modal";

          const flipBtn = document.createElement("button");
          flipBtn.innerText = "🔄 Flip Camera";
          flipBtn.onclick = () => {
            stream.getTracks().forEach((t) => t.stop());
            setFacingMode((f) => (f === "user" ? "environment" : "user"));
            document.body.removeChild(modal);
            handleCameraOption(roomId, "camera");
          };

          const snapBtn = document.createElement("button");
          snapBtn.innerText = "📸 Take Photo";
          snapBtn.onclick = () => {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext("2d").drawImage(video, 0, 0);
            const dataUrl = canvas.toDataURL("image/jpeg");
            stream.getTracks().forEach((t) => t.stop());
            updateRoomImage(roomId, dataUrl);
            document.body.removeChild(modal);
          };

          const closeBtn = document.createElement("button");
          closeBtn.innerText = "❌ Cancel";
          closeBtn.onclick = () => {
            stream.getTracks().forEach((t) => t.stop());
            document.body.removeChild(modal);
          };

          modal.append(flipBtn, video, snapBtn, closeBtn);
          document.body.appendChild(modal);
        });
    } else {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onloadend = () => updateRoomImage(roomId, reader.result);
          reader.readAsDataURL(file);
        }
      };
      input.click();
    }
  }

  async function handleCreateProfile() {
    const userId = AWS.config.credentials.identityId;
    const awsCreds = AWS.config.credentials;
    setIsCreating(true);
    await saveLivingHarmonyProfile(userId, awsCreds, form, roomOptions);
    setIsCreating(false);
  }

  async function saveProfile(e) {
    if (user) {
      e.preventDefault();
      await handleCreateProfile();
    }
  }

  if (loading) return <div>Loading…</div>;

  return (
    <div className="profile-page min-h-screen bg-white px-6 py-12">
      {isLinkedInInApp && (
        <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 rounded text-center">
          ⚠️ Please open this page in your device’s default browser for Google
          sign-in to work.
        </div>
      )}

      <h1 className="text-3xl font-bold mb-6 text-center">
        {user ? "Edit Profile" : "Login with Google to Create Profile"}
      </h1>

      {user ? (
        <Link to="/" className="text-blue-500 hover:underline">
          ← Back to Home
        </Link>
      ) : (
        ""
      )}

      {!user ? (
        <div className="footer text-center">
          <button onClick={signInWithGoogle} className="footer-button">
            Login with Google
          </button>
        </div>
      ) : (
        <form className="max-w-2xl mx-auto space-y-8" onSubmit={saveProfile}>
          {/* ───────────────────── Section: Basic Info ───────────────────── */}
          <section>
            <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Full Name
                </label>
                <input
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  name="email"
                  value={form.email}
                  disabled
                  className="w-full border border-gray-200 bg-gray-100 text-gray-600 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Location
                </label>
                <input
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Home Type
                </label>
                <select
                  name="homeType"
                  value={form.homeType}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="">Select...</option>
                  <option>Apartment</option>
                  <option>House</option>
                  <option>Condo</option>
                  <option>Tiny Home</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
          </section>

          <hr className="border-t border-gray-200" />

          {/* ───────────────────── Section: Household ───────────────────── */}
          <section>
            <h2 className="text-xl font-semibold mb-4">
              Who Do You Live With?
            </h2>
            <table className="profile-living-table w-full text-left">
              <tbody>
                {[
                  ["🐾 Pets", "🧒 Young Children"],
                  ["🧑‍🎓 Teenagers", "👵 Older Parents"],
                  ["👯 Roommates", "🧘 Alone"],
                ].map((row, i) => (
                  <tr key={i}>
                    {row.map((labelText) => {
                      const value = labelText.replace(/^[^a-zA-Z]+/, "").trim();
                      return (
                        <td key={value} className="py-2 pr-6">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              name="livingWith"
                              value={value}
                              checked={form.livingWith.includes(value)}
                              onChange={handleChange}
                            />
                            <span>{labelText}</span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <hr className="border-t border-gray-200" />

          {/* ───────────────────── Section: Notes ───────────────────── */}
          <section>
            <h2 className="text-xl font-semibold mb-4">Notes</h2>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={4}
              className="w-full border border-gray-300 rounded-md p-2"
              placeholder="Tell us anything about your space, your style, or what you're dreaming about..."
            />
          </section>

          {/* ───────────────────── Single Save Button ───────────────────── */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isCreating}
              className="w-full py-3 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {isCreating ? "Saving…" : "Save Profile"}
            </button>
          </div>

          {/* Optional secondary actions */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleLogout}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
            >
              Logout
            </button>
          </div>

          <div className="agents-view text-center mt-4">
            <Link
              to="/agents"
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
            >
              View my Agents
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
